import { useUnit } from 'effector-react';
import { useLocation } from 'react-router-dom';
import { $catalogCategories, $catalogProducts, $cityPhoneData } from 'domains/catalog';
import { $city, $country, $restaurantsList } from 'domains/cartography';
import FeatSDKV1 from 'api/version1';
import { $seoPublicOrigin } from './model';
import { buildCanonicalUrl, isPublicSeoRoute, normalizeSeoPath } from './urls';
import { buildOrganizationSchema, buildRestaurantSchema, SiteSchema } from './structuredData';
import { SeoTags } from './SeoTags';
import { Helmet } from 'react-helmet';
import { isNoIndexRequest } from './indexing';
import { buildCatalogSeoMeta } from './meta';

export const SiteSeo = () => {
  const { pathname, search } = useLocation();
  const [origin, categories, products, city, country, restaurants, phone] = useUnit([
    $seoPublicOrigin, $catalogCategories, $catalogProducts, $city, $country,
    $restaurantsList, $cityPhoneData,
  ]);
  const path = normalizeSeoPath(pathname);
  const catalogProducts = [...(products?.dishes || []), ...(products?.constructors || [])];
  const productSlugs = catalogProducts
    .map((product) => product.linkValue?.value);
  const noindex = isNoIndexRequest(path, search);
  if (!isPublicSeoRoute(path, categories.map((category) => category.linkValue), productSlugs)) {
    return noindex ? <Helmet><meta name='robots' content='noindex,follow' /></Helmet> : null;
  }

  const schema: SiteSchema = {
    '@context': 'https://schema.org',
    '@graph': [buildOrganizationSchema(origin)],
  };
  if (path === '/restaurants' && city) {
    const ids = new Set<string>();
    (Array.isArray(restaurants) ? restaurants : []).forEach((restaurant) => {
      if (!restaurant || ids.has(restaurant.id)) return;
      const node = buildRestaurantSchema({
        restaurant, city, country, origin, telephone: phone.phoneNumber,
        imageUrls: (Array.isArray(restaurant.imageIds) ? restaurant.imageIds : [])
          .filter((id) => typeof id === 'string' && !!id).map(FeatSDKV1.images.getImage),
      });
      if (node) { schema['@graph'].push(node); ids.add(restaurant.id); }
    });
  }
  const slug = path === '/' ? null : decodeURIComponent(path.slice(1));
  const currentCategory = slug
    ? categories.find((category) => category.linkValue === slug) || null
    : null;
  const currentProduct = !currentCategory && slug
    ? catalogProducts.find((product) => product.linkValue?.value === slug) || null
    : null;
  const productCategory = currentProduct
    ? categories.find((category) => category.id === currentProduct.categoryId) || null
    : null;
  const meta = path === '/' || currentCategory || currentProduct
    ? buildCatalogSeoMeta({
      cityName: city?.name,
      categoryName: (productCategory || currentCategory)?.name,
      productName: currentProduct?.name,
    })
    : null;

  return (
    <SeoTags
      canonical={buildCanonicalUrl(path, origin)}
      schema={schema}
      noindex={noindex}
      title={meta?.title}
      description={meta?.description}
    />
  );
};
